import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Textarea } from '@/design-system/textarea';
import {
  reasonsAPI,
  CategorizedReasons,
  ReasonOption,
} from '@/services/api/reasons';
import { getListDrivers, Driver } from '@/services/api/drivers';
import { DeliveryDetail } from '@/services/api/deliveries';

interface AssignDriverModalProps {
  isOpen: boolean;
  onClose: () => void;
  deliveryData: DeliveryDetail | null;
  onAssign: (
    driverId: string,
    notes: string,
    reasonCode: string | null
  ) => Promise<void>;
  isLoading?: boolean;
}

interface DriverOption {
  value: string;
  label: string;
  phone: string;
  tags: Array<{ key: string; name: string }>;
  partner?: string;
  vehicle?: string;
}

const labelCls = 'mb-1 block text-[11px] font-medium text-text-sub-600';

/** Inline hook: fetches drivers with the richer fields the modal needs
 *  (phone, tags, partner, vehicle). Kept local so the shared
 *  `useDriverOptions` stays a plain {value,label} loader for other callers. */
function useDriverOptionsRich(open: boolean) {
  const [options, setOptions] = useState<DriverOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) {
      setSearch('');
      setOptions([]);
      return;
    }
    let cancelled = false;
    const trimmed = search.trim();
    const fire = () => {
      setLoading(true);
      getListDrivers({
        page: '1',
        size: '50',
        sort: 'created_at',
        order: 'desc',
        active: 'true',
        search: trimmed || '',
      })
        .then((res) => {
          if (cancelled) return;
          setOptions(res.data.map(driverToOption));
        })
        .catch(() => {
          if (!cancelled) setOptions([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    const timer = trimmed ? setTimeout(fire, 400) : (fire(), undefined);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, search]);

  return { options, loading, search, setSearch };
}

function driverToOption(d: Driver): DriverOption {
  const fullName =
    [d.first_name, d.last_name].filter(Boolean).join(' ') || d.name;
  const phone = d.phone_number || d.phoneNumber || d.phone || d.mobile || '';
  const rawTags = Array.isArray(d.tags) ? d.tags : [];
  const tags = rawTags
    .map((t, i) =>
      typeof t === 'object'
        ? { key: String(t.id ?? `${i}-${t.name}`), name: t.name }
        : { key: `${i}-${t}`, name: String(t) }
    )
    .filter((t) => t.name);
  return {
    value: String(d.id),
    label: fullName,
    phone,
    tags,
    partner: d.partner?.name || undefined,
    vehicle: d.vehicle?.type || undefined,
  };
}

const AssignDriverModal: React.FC<AssignDriverModalProps> = ({
  isOpen,
  onClose,
  deliveryData,
  onAssign,
  isLoading = false,
}) => {
  const drivers = useDriverOptionsRich(isOpen);

  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [selectedDriverLabel, setSelectedDriverLabel] = useState('');
  const [reasons, setReasons] = useState<CategorizedReasons>({});
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedReason, setSelectedReason] = useState<ReasonOption | null>(
    null
  );
  const [customNotes, setCustomNotes] = useState('');

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputWrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [anchor, setAnchor] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);

  const currentDriverId = deliveryData?.courier?.id
    ? String(deliveryData.courier.id)
    : null;

  const isDriverChanged =
    selectedDriverId !== '' && selectedDriverId !== currentDriverId;

  // Reset/preselect when opening
  useEffect(() => {
    if (!isOpen) {
      setSelectedDriverId('');
      setSelectedDriverLabel('');
      setSelectedReason(null);
      setCustomNotes('');
      setReasons({});
      setDropdownOpen(false);
      return;
    }
    if (deliveryData?.courier?.id) {
      setSelectedDriverId(String(deliveryData.courier.id));
      setSelectedDriverLabel(deliveryData.courier.name);
    }
  }, [isOpen, deliveryData]);

  // Pre-fetch reasons on open
  useEffect(() => {
    if (!isOpen) {
      setReasons({});
      setSelectedReason(null);
      return;
    }
    setLoadingReasons(true);
    const type = deliveryData?.courier?.id ? 'REASSIGNMENT' : 'ASSIGNMENT';
    reasonsAPI
      .getCategorized({
        state: deliveryData?.internalState ?? 'UNCATEGORIZED',
        type,
        language: 'ID',
      })
      .then((data) => setReasons(data))
      .catch(() => setReasons({}))
      .finally(() => setLoadingReasons(false));
  }, [isOpen, deliveryData]);

  // Position the portal dropdown relative to the input. Re-measure on
  // scroll/resize so it tracks the modal body's scroll position.
  const recompute = useCallback(() => {
    const el = inputWrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchor({ left: r.left, top: r.bottom, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (!dropdownOpen) return;
    recompute();
    const handler = () => recompute();
    window.addEventListener('scroll', handler, true); // capture catches inner scrollers
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [dropdownOpen, recompute]);

  // Click outside the dropdown / input closes it
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        inputWrapRef.current?.contains(target) ||
        document.getElementById('driver-dropdown-panel')?.contains(target)
      ) {
        return;
      }
      setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const isOther =
    selectedReason?.internalReason?.toLowerCase().includes('lainnya') ||
    selectedReason?.internalReason?.toLowerCase().includes('others');

  const canSave =
    selectedDriverId !== '' &&
    selectedDriverId !== currentDriverId &&
    !loadingReasons &&
    selectedReason !== null &&
    (!isOther || customNotes.trim().length >= 3);

  const handleSave = async () => {
    if (!selectedDriverId) return;
    const notes = isOther
      ? customNotes.trim()
      : (selectedReason?.internalReason ?? '');
    await onAssign(selectedDriverId, notes, selectedReason?.code ?? null);
  };

  const handlePickDriver = (opt: DriverOption) => {
    setSelectedDriverId(opt.value);
    setSelectedDriverLabel(opt.label);
    drivers.setSearch('');
    setDropdownOpen(false);
  };

  const handleClear = () => {
    setSelectedDriverId('');
    setSelectedDriverLabel('');
    setSelectedReason(null);
    drivers.setSearch('');
    setDropdownOpen(true);
    // Re-focus the input after clearing so user can keep typing
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const title = deliveryData?.courier ? 'Ganti Driver' : 'Assign Driver';
  const subtitle = deliveryData?.courier
    ? 'Pilih driver baru untuk menggantikan driver sebelumnya. Penggantian ini akan langsung tercatat pada sistem.'
    : 'Pilih driver untuk ditugaskan ke pengiriman ini.';

  const filteredOptions = drivers.options.filter(
    (o) => o.value !== currentDriverId
  );

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-user-line" />}
      iconTone="primary"
      title={title}
      subtitle={subtitle}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            size="sm"
            onClick={onClose}
            disabled={isLoading}
          >
            Batal
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!canSave || isLoading}
            loading={isLoading}
          >
            {isLoading ? 'Memproses…' : 'Simpan'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Driver search */}
        <div>
          <p className={labelCls}>Nama Driver</p>
          <div ref={inputWrapRef} className="relative">
            <input
              ref={inputRef}
              type="text"
              value={selectedDriverLabel}
              onChange={(e) => {
                setSelectedDriverLabel(e.target.value);
                drivers.setSearch(e.target.value);
                setSelectedDriverId('');
                setSelectedReason(null);
                setDropdownOpen(true);
              }}
              onFocus={() => setDropdownOpen(true)}
              placeholder="Cari driver…"
              className="w-full rounded-lg border border-stroke-soft-200 pl-3 pr-16 py-2 text-sm text-text-strong-950 placeholder:text-text-soft-400 focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
            />
            {/* Clear (X) button */}
            {selectedDriverLabel && (
              <button
                type="button"
                onClick={handleClear}
                aria-label="Clear driver"
                className="absolute right-8 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded-full text-text-soft-400 hover:bg-bg-weak-50 hover:text-text-sub-600 transition-colors"
              >
                <i className="ri-close-line text-[14px]" />
              </button>
            )}
            {/* Chevron */}
            <button
              type="button"
              onClick={() => {
                setDropdownOpen((o) => !o);
                if (!dropdownOpen) inputRef.current?.focus();
              }}
              aria-label="Toggle driver list"
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center text-text-soft-400 hover:text-text-sub-600 transition-colors"
            >
              <i
                className={`ri-arrow-down-s-line text-[18px] transition-transform ${
                  dropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>
          {selectedDriverId && !dropdownOpen && (
            <p className="mt-1 text-[11px] text-accent font-semibold">
              <i className="ri-check-line mr-0.5" />
              Driver dipilih
            </p>
          )}
        </div>

        {/* Reason section — only if driver changed */}
        {isDriverChanged && (
          <div>
            <p className={labelCls}>Alasan penggantian driver</p>
            {loadingReasons ? (
              <div className="flex items-center gap-2 py-3 text-sm text-text-soft-400">
                <i className="ri-loader-4-line animate-spin" />
                Loading…
              </div>
            ) : (
              <div className="space-y-3 max-h-48 overflow-y-auto pr-1">
                {Object.entries(reasons).map(([category, options]) => (
                  <div key={category}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-text-soft-400 mb-1.5">
                      {category}
                    </p>
                    <div className="space-y-2 ml-1">
                      {options.map((opt) => (
                        <label
                          key={opt.id}
                          className="flex items-start gap-2.5 cursor-pointer group"
                        >
                          <input
                            type="radio"
                            name="driver-reason"
                            value={String(opt.id)}
                            checked={selectedReason?.id === opt.id}
                            onChange={() => {
                              setSelectedReason(opt);
                              setCustomNotes('');
                            }}
                            className="mt-0.5 accent-accent shrink-0"
                          />
                          <span className="text-sm text-text-sub-600 group-hover:text-text-strong-950 leading-snug">
                            {opt.internalReason}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isOther && (
              <div className="mt-3">
                <Textarea
                  value={customNotes}
                  onChange={(e) => setCustomNotes(e.target.value)}
                  maxLength={200}
                  rows={3}
                  placeholder="Jelaskan alasan penggantian…"
                  className="resize-none"
                />
                <p className="mt-1 text-right text-[11px] text-text-soft-400">
                  {customNotes.length}/200
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Portal-rendered dropdown — escapes the modal's overflow:auto */}
      {dropdownOpen &&
        anchor &&
        createPortal(
          <div
            id="driver-dropdown-panel"
            data-searchable-dropdown-portal=""
            style={{
              position: 'fixed',
              left: anchor.left,
              top: anchor.top + 4,
              width: anchor.width,
              zIndex: 9999,
              // Body-portaled: re-enable clicks + let FormModal's outside-guard
              // recognise this panel so picking a driver doesn't close the modal.
              pointerEvents: 'auto',
            }}
            className="max-h-72 overflow-y-auto rounded-lg border border-stroke-soft-200 bg-bg-white-0 shadow-xl"
          >
            {drivers.loading ? (
              <div className="flex items-center gap-2 px-4 py-3 text-sm text-text-soft-400">
                <i className="ri-loader-4-line animate-spin" />
                Loading…
              </div>
            ) : filteredOptions.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-text-soft-400">
                {drivers.search
                  ? `Tidak ada driver cocok dengan "${drivers.search}"`
                  : 'Tidak ada driver tersedia'}
              </div>
            ) : (
              filteredOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handlePickDriver(opt)}
                  className="flex w-full flex-col items-start gap-1 px-4 py-2.5 text-left hover:bg-bg-weak-50 border-b border-stroke-soft-200 last:border-b-0"
                >
                  <div className="flex w-full items-baseline gap-1.5">
                    <span className="text-sm font-medium text-text-strong-950 truncate">
                      {opt.label}
                    </span>
                    {opt.phone && (
                      <span className="text-[12px] font-mono text-text-sub-600 truncate">
                        ({opt.phone})
                      </span>
                    )}
                  </div>
                  {(opt.tags.length > 0 || opt.partner || opt.vehicle) && (
                    <div className="flex flex-wrap items-center gap-1">
                      {opt.partner && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-accent/10 text-accent">
                          {opt.partner}
                        </span>
                      )}
                      {opt.vehicle && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-(--state-information-lighter) text-(--state-information-dark)">
                          {opt.vehicle}
                        </span>
                      )}
                      {opt.tags.slice(0, 5).map((t) => (
                        <span
                          key={t.key}
                          className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-bg-weak-50 text-text-sub-600"
                        >
                          {t.name}
                        </span>
                      ))}
                      {opt.tags.length > 5 && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-bg-soft-200 text-text-sub-600">
                          +{opt.tags.length - 5}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </FormModal>
  );
};

export default AssignDriverModal;
